import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Lottie from 'lottie-react';
import frontIlustAnimation from './lotties/HomeCleanSection/front_ilust.json';
import backIlustAnimation from './lotties/HomeCleanSection/back_ilust.json';
import useBreakpoints from '../hooks/useBreakpoints';

const backIlustXRange = {
  desktop: [16, 3],
  tablet: [6, -6],
  mobile: [4, -6],
};

const frontIlustXRange = {
  desktop: [-40, -20],
  tablet: [-40, -30],
  mobile: [-40, -30],
};

const ilustYRange = {
  desktop: [0, 10],
  tablet: [0, 10],
  mobile: [0, 10],
};

export default function HomeCleanSection() {
  const media = useBreakpoints({ defaultMedia: 'desktop' });
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const backIlustX = useSpring(useTransform(scrollYProgress, [0, 0.4], backIlustXRange[media]), {
    stiffness: 600,
    damping: 100,
  });

  const frontIlustX = useSpring(useTransform(scrollYProgress, [0, 0.4], frontIlustXRange[media]), {
    stiffness: 600,
    damping: 100,
  });

  const ilustY = useSpring(useTransform(scrollYProgress, [0.7, 1], ilustYRange[media]), {
    stiffness: 600,
    damping: 100,
  });

  const backIlustTransform = useMotionTemplate`translateX(${backIlustX}%)`;
  const frontIlustTransform = useMotionTemplate`translateX(${frontIlustX}%)`;
  const ilustYTransform = useMotionTemplate`translateY(${ilustY}%)`;

  return (
    <Container ref={containerRef}>
      <Wrapper>
        <Content>
          <TitleWrapper>
            <FakeTitle>우리집 청소는 청소연구소</FakeTitle>
            <TitleDesktop>{TitleDesktopSvg}</TitleDesktop>
            <TitleTablet>{TitleTabletSvg}</TitleTablet>
          </TitleWrapper>
          <Info>
            <Description>
              해도 해도 끝없는 집안일. 예약 한 번이면 청소 끝! <Br /> 5단계 집청소 교육을 수료한 매니저가 표준화된
              <Br /> 청소 매뉴얼로 우리집을 청소합니다.
            </Description>
            <Link href="/home_clean" passHref>
              <Button>더 알아보기</Button>
            </Link>
          </Info>
        </Content>
        <Ilust>
          <IlustWrapper style={{ transform: ilustYTransform }}>
            <BackIlust style={{ transform: backIlustTransform }}>
              <Lottie animationData={backIlustAnimation} loop autoPlay rendererSettings={{}} />
            </BackIlust>
            <FrontIlust style={{ transform: frontIlustTransform }}>
              <Lottie animationData={frontIlustAnimation} loop autoPlay rendererSettings={{}} />
            </FrontIlust>
          </IlustWrapper>
        </Ilust>
      </Wrapper>
    </Container>
  );
}

const Container = styled.section`
  width: 100%;
  padding: 0 24px;
  padding-top: 120px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding-top: 70px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding-top: 74px;
  }
`;

const Wrapper = styled.div`
  margin: 0 auto;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.desktop};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths!.tablet};
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 83px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-bottom: 70px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    flex-direction: column;
    margin-bottom: 60px;
  }
`;

const TitleWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 14px;
  }
`;

const FakeTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const TitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const TitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const Info = styled.div``;

const Description = styled.h2`
  word-break: keep-all;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  margin-bottom: 30px;
  width: 486px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    width: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-bottom: 44px;
    width: auto;
    word-break: normal;
    font-weight: 400;
  }
`;

const Br = styled.br`
  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    display: none;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #999999;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
    border-color: #42484c;
  }
`;

const Ilust = styled.div`
  border-radius: 16px;
  background: #f5f5f5;
  overflow: hidden;
  isolation: isolate;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    border-radius: 8px;
  }
`;

const IlustWrapper = styled(motion.div)`
  position: relative;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    transform-origin: 100% 90%;
    scale: 1.3;
  }
`;

const BackIlust = styled(motion.div)`
  position: relative;
  left: 0;
  top: 0;
  width: 1630px;
  height: 440px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 1500px;
    height: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: 200%;
    height: auto;
  }
`;

const FrontIlust = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 0;
  width: 1630px;
  height: 440px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    width: 1500px;
    height: 405px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    width: 200%;
    height: auto;
    min-height: 240px;

    div {
      min-height: 240px;
    }
  }
`;

const TitleDesktopSvg = (
  <svg width="259" height="105" viewBox="0 0 259 105" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27.0159 11.516C27.0159 14.92 23.9339 17.634 19.7479 17.634C15.4239 17.634 12.4799 14.92 12.4799 11.516C12.4799 8.02 15.4239 5.536 19.7479 5.536C23.9339 5.536 27.0159 8.02 27.0159 11.516ZM22.2779 42.474V28.674H39.1139V24.35H0.381891V28.674H17.2639V42.474H22.2779ZM32.2599 11.516C32.2599 5.076 26.7399 0.981998 19.7479 0.981998C12.9399 0.981998 7.23589 5.03 7.23589 11.516C7.23589 18.324 13.2619 22.004 19.7479 22.004C26.1419 22.004 32.2599 18.324 32.2599 11.516ZM68.6423 23.43C63.3523 24.534 54.4283 24.994 49.5523 24.764V17.726H63.8583V2.868H44.3543V7.284H58.7523V13.402H44.4463V29.042C52.0823 29.41 63.0303 29.134 69.1943 27.708L68.6423 23.43ZM72.5063 39.07H77.5663V0.751998H72.5063V39.07ZM114.363 39.852H99.6427V34.976H114.363V39.852ZM114.363 23.66H119.469V0.659999H114.363V23.66ZM94.3067 6.916C94.2607 9.998 93.3867 12.62 91.5927 14.736C89.7987 16.898 87.3607 18.508 84.2787 19.612L86.6707 23.798C91.2707 21.958 95.3647 18.646 97.0207 14.552C99.0447 18.554 103.093 21.636 107.555 23.108L110.039 18.968C106.865 17.956 104.335 16.438 102.449 14.414C100.655 12.436 99.6887 9.906 99.6427 6.916H107.969V2.546H85.6127V6.916H94.3067ZM119.469 44.222V25.96H114.363V30.698H99.6427V25.96H94.5367V44.222H119.469ZM156.048 5.214V0.843999H142.892V5.214H156.048ZM167.134 35.804C167.134 38.748 164.558 40.588 160.74 40.588C157.06 40.588 154.438 38.748 154.438 35.804C154.438 32.86 157.06 31.02 160.74 31.02C164.558 31.02 167.134 32.86 167.134 35.804ZM172.838 26.144V0.614H167.732V13.862H160.464V18.14H167.732V26.144H172.838ZM146.71 12.206C146.618 13.31 146.388 14.276 145.974 15.15C144.41 18.692 140.868 20.808 136.912 22.326L139.304 26.374C143.858 25.04 148.182 21.36 149.608 18.048C151.08 21.314 155.726 24.304 159.774 25.546L161.982 21.406C157.888 20.302 154.484 18.14 153.104 14.874C152.782 14.092 152.552 13.172 152.46 12.206H160.786V7.882H138.108V12.206H146.71ZM172.286 35.804C172.286 30.514 167.502 26.696 160.74 26.696C154.116 26.696 149.286 30.514 149.286 35.804C149.286 41.186 154.116 44.958 160.74 44.958C167.502 44.958 172.286 41.186 172.286 35.804ZM216.672 34.976V30.56H199.836V21.636H194.822V30.56H177.94V34.976H216.672ZM194.362 1.81V4.34C194.362 11.7 188.06 16.944 180.792 18.784L183.184 23.2C189.624 21.084 195.144 17.266 197.306 12.252C199.698 17.22 204.712 21.13 211.474 23.154L213.82 18.738C206.644 16.76 200.25 11.746 200.25 4.34V1.81H194.362ZM258.068 25.546V21.222H219.336V25.546H258.068ZM225.868 1.12V16.622H252.18V12.206H231.02V1.12H225.868ZM225.638 29.134V43.026H252.134V38.702H230.698V29.134H225.638ZM19.8859 65.214V60.844H6.72989V65.214H19.8859ZM30.9719 95.804C30.9719 98.748 28.3959 100.588 24.5779 100.588C20.8979 100.588 18.2759 98.748 18.2759 95.804C18.2759 92.86 20.8979 91.02 24.5779 91.02C28.3959 91.02 30.9719 92.86 30.9719 95.804ZM36.6759 86.144V60.614H31.5699V73.862H24.3019V78.14H31.5699V86.144H36.6759ZM10.5479 72.206C10.4559 73.31 10.2259 74.276 9.81189 75.15C8.24789 78.692 4.70589 80.808 0.749891 82.326L3.14189 86.374C7.69589 85.04 12.0199 81.36 13.4459 78.048C14.9179 81.314 19.5639 84.304 23.6119 85.546L25.8199 81.406C21.7259 80.302 18.3219 78.14 16.9419 74.874C16.6199 74.092 16.3899 73.172 16.2979 72.206H24.6239V67.882H1.94589V72.206H10.5479ZM36.1239 95.804C36.1239 90.514 31.3399 86.696 24.5779 86.696C17.9539 86.696 13.1239 90.514 13.1239 95.804C13.1239 101.186 17.9539 104.958 24.5779 104.958C31.3399 104.958 36.1239 101.186 36.1239 95.804ZM80.5103 94.976V90.56H63.6743V81.636H58.6603V90.56H41.7783V94.976H80.5103ZM58.2003 61.81V64.34C58.2003 71.7 51.8983 76.944 44.6303 78.784L47.0223 83.2C53.4623 81.084 58.9823 77.266 61.1443 72.252C63.5363 77.22 68.5503 81.13 75.3123 83.154L77.6583 78.738C70.4823 76.76 64.0883 71.746 64.0883 64.34V61.81H58.2003ZM114.317 66.502H103.553C101.529 64.386 98.5847 63.052 95.1347 63.052C88.6027 63.052 83.9107 67.698 83.9107 73.632C83.9107 79.704 88.6027 84.212 95.1347 84.212C98.8147 84.212 101.943 82.786 103.921 80.394H114.317V90.284H119.469V60.706H114.317V66.502ZM120.067 102.934V98.518H99.2747V87.8H94.1687V102.934H120.067ZM101.299 73.586C101.299 77.082 98.6307 79.796 95.1347 79.796C91.5467 79.796 88.9707 77.082 88.9707 73.586C88.9707 70.09 91.5467 67.514 95.1347 67.514C98.6307 67.514 101.299 70.09 101.299 73.586ZM106.359 73.632C106.359 72.758 106.221 71.838 106.037 71.01H114.317V75.978H106.129C106.267 75.242 106.359 74.46 106.359 73.632ZM146.559 102.014V85.592H163.303V81.13H155.621C155.851 80.256 156.035 79.244 156.173 78.186C156.909 73.586 157.415 67.928 157.139 62.27H130.275V66.732H152.033C152.125 69.86 151.757 74.414 151.113 78.37L150.607 81.13H124.571V85.592H141.453V102.014H146.559ZM204.7 94.976V90.56H187.864V81.636H182.85V90.56H165.968V94.976H204.7ZM182.39 61.81V64.34C182.39 71.7 176.088 76.944 168.82 78.784L171.212 83.2C177.652 81.084 183.172 77.266 185.334 72.252C187.726 77.22 192.74 81.13 199.502 83.154L201.848 78.738C194.672 76.76 188.278 71.746 188.278 64.34V61.81H182.39Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const TitleTabletSvg = (
  <svg width="156" height="65" viewBox="0 0 156 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.3383 7.042C16.3383 9.04 14.5293 10.633 12.0723 10.633C9.53434 10.633 7.80634 9.04 7.80634 7.042C7.80634 4.99 9.53434 3.532 12.0723 3.532C14.5293 3.532 16.3383 4.99 16.3383 7.042ZM13.5573 25.213V17.113H23.4393V14.575H0.705344V17.113H10.6143V25.213H13.5573ZM19.4163 7.042C19.4163 3.262 16.1763 0.858999 12.0723 0.858999C8.07634 0.858999 4.72834 3.235 4.72834 7.042C4.72834 11.038 8.26534 13.198 12.0723 13.198C15.8253 13.198 19.4163 11.038 19.4163 7.042ZM41.3112 14.035C38.2062 14.683 32.9682 14.953 30.1062 14.818V10.687H38.5032V1.966H27.0552V4.558H35.5062V8.149H27.1092V17.329C31.5912 17.545 38.0172 17.383 41.6352 16.546L41.3112 14.035ZM43.5792 23.215H46.5492V0.723999H43.5792V23.215ZM68.6871 23.674H60.0471V20.812H68.6871V23.674ZM68.6871 14.17H71.6841V0.669999H68.6871V14.17ZM56.9151 4.342C56.8881 6.151 56.3751 7.69 55.3221 8.932C54.2691 10.201 52.8381 11.146 51.0291 11.794L52.4331 14.251C55.1331 13.171 57.5361 11.227 58.5081 8.824C59.6961 11.173 62.0721 12.982 64.6911 13.846L66.1491 11.416C64.2861 10.822 62.8011 9.931 61.6941 8.743C60.6411 7.582 60.0741 6.097 60.0471 4.342H64.9341V1.777H51.8121V4.342H56.9151ZM71.6841 26.239V15.52H68.6871V18.301H60.0471V15.52H57.0501V26.239H71.6841ZM94.2344 3.343V0.778H86.5124V3.343H94.2344ZM100.741 21.298C100.741 23.026 99.2294 24.106 96.9884 24.106C94.8284 24.106 93.2894 23.026 93.2894 21.298C93.2894 19.57 94.8284 18.49 96.9884 18.49C99.2294 18.49 100.741 19.57 100.741 21.298ZM104.089 15.628V0.643H101.092V8.419H96.8264V10.93H101.092V15.628H104.089ZM88.7534 7.447C88.6994 8.095 88.5644 8.662 88.3214 9.175C87.4034 11.254 85.3244 12.496 83.0024 13.387L84.4064 15.763C87.0794 14.98 89.6174 12.82 90.4544 10.876C91.3184 12.793 94.0454 14.548 96.4214 15.277L97.7174 12.847C95.3144 12.199 93.3164 10.93 92.5064 9.013C92.3174 8.554 92.1824 8.014 92.1284 7.447H97.0154V4.909H83.7044V7.447H88.7534ZM103.765 21.298C103.765 18.193 100.957 15.952 96.9884 15.952C93.1004 15.952 90.2654 18.193 90.2654 21.298C90.2654 24.457 93.1004 26.671 96.9884 26.671C100.957 26.671 103.765 24.457 103.765 21.298ZM130.358 20.812V18.22H120.476V12.982H117.533V18.22H107.624V20.812H130.358ZM117.263 1.345V2.83C117.263 7.15 113.564 10.228 109.298 11.308L110.702 13.9C114.482 12.658 117.722 10.417 118.991 7.474C120.395 10.39 123.338 12.685 127.307 13.873L128.684 11.281C124.472 10.12 120.719 7.177 120.719 2.83V1.345H117.263ZM155.196 15.277V12.739H132.462V15.277H155.196ZM136.296 0.939999V10.039H151.74V7.447H139.32V0.939999H136.296ZM136.161 17.383V25.537H151.713V22.999H139.131V17.383H136.161ZM12.1533 41.343V38.778H4.43134V41.343H12.1533ZM18.6603 59.298C18.6603 61.026 17.1483 62.106 14.9073 62.106C12.7473 62.106 11.2083 61.026 11.2083 59.298C11.2083 57.57 12.7473 56.49 14.9073 56.49C17.1483 56.49 18.6603 57.57 18.6603 59.298ZM22.0083 53.628V38.643H19.0113V46.419H14.7453V48.93H19.0113V53.628H22.0083ZM6.67234 45.447C6.61834 46.095 6.48334 46.662 6.24034 47.175C5.32234 49.254 3.24334 50.496 0.921344 51.387L2.32534 53.763C4.99834 52.98 7.53634 50.82 8.37334 48.876C9.23734 50.793 11.9643 52.548 14.3403 53.277L15.6363 50.847C13.2333 50.199 11.2353 48.93 10.4253 47.013C10.2363 46.554 10.1013 46.014 10.0473 45.447H14.9343V42.909H1.62334V45.447H6.67234ZM21.6843 59.298C21.6843 56.193 18.8763 53.952 14.9073 53.952C11.0193 53.952 8.18434 56.193 8.18434 59.298C8.18434 62.457 11.0193 64.671 14.9073 64.671C18.8763 64.671 21.6843 62.457 21.6843 59.298ZM48.2772 58.812V56.22H38.3952V50.982H35.4522V56.22H25.5432V58.812H48.2772ZM35.1822 39.345V40.83C35.1822 45.15 31.4832 48.228 27.2172 49.308L28.6212 51.9C32.4012 50.658 35.6412 48.417 36.9102 45.474C38.3142 48.39 41.2572 50.685 45.2262 51.873L46.6032 49.281C42.3912 48.12 38.6382 45.177 38.6382 40.83V39.345H35.1822ZM68.6601 42.099H62.3421C61.1541 40.857 59.4261 40.074 57.4011 40.074C53.5671 40.074 50.8131 42.801 50.8131 46.284C50.8131 49.848 53.5671 52.494 57.4011 52.494C59.5611 52.494 61.3971 51.657 62.5581 50.253H68.6601V56.058H71.6841V38.697H68.6601V42.099ZM72.0351 63.483V60.891H59.8311V54.6H56.8341V63.483H72.0351ZM61.0191 46.257C61.0191 48.309 59.4531 49.902 57.4011 49.902C55.2951 49.902 53.7831 48.309 53.7831 46.257C53.7831 44.205 55.2951 42.693 57.4011 42.693C59.4531 42.693 61.0191 44.205 61.0191 46.257ZM63.9891 46.284C63.9891 45.771 63.9081 45.231 63.8001 44.745H68.6601V47.661H63.8541C63.9351 47.229 63.9891 46.77 63.9891 46.284ZM88.125 62.943V53.304H97.953V50.685H93.444C93.579 50.172 93.687 49.578 93.768 48.957C94.2 46.257 94.497 42.936 94.335 39.615H78.567V42.234H91.338C91.392 44.07 91.176 46.743 90.798 49.065L90.501 50.685H75.219V53.304H85.128V62.943H88.125ZM122.791 58.812V56.22H112.909V50.982H109.966V56.22H100.057V58.812H122.791ZM109.696 39.345V40.83C109.696 45.15 105.997 48.228 101.731 49.308L103.135 51.9C106.915 50.658 110.155 48.417 111.424 45.474C112.828 48.39 115.771 50.685 119.74 51.873L121.117 49.281C116.905 48.12 113.152 45.177 113.152 40.83V39.345H109.696Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
